import PropTypes from "prop-types";
import MetaTags from "react-meta-tags";
import React, { useState, useEffect } from "react";
import { Alert } from "reactstrap";
import { withRouter, Link } from "react-router-dom";
import { post } from "helpers/api_helper";

// Icons from react-icons
import {
  RiToothLine,
  RiShieldCheckLine,
  RiLockPasswordLine,
  RiKeyLine,
  RiEyeLine,
  RiEyeOffLine,
  RiArrowRightLine,
  RiMailLine,
  RiCheckDoubleLine,
  RiShieldKeyholeLine,
} from "react-icons/ri";

import oralstopLogo from "../../assets/images/logos/oralstop-logo.png";

import "./auth-custom.scss";

const ResetPassword = (props) => {
  const [formData, setFormData] = useState({
    token: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const searchParams = new URLSearchParams(props.location.search);
    const queryToken = searchParams.get("token") || "";
    const queryEmail = searchParams.get("email") || "";

    if (queryToken || queryEmail) {
      setFormData((prev) => ({
        ...prev,
        token: queryToken,
        email: queryEmail,
      }));
    }
  }, [props.location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errorMessage) setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.token.trim() && !formData.email.trim()) {
      setErrorMessage("Please enter your verification token or registered email address.");
      return;
    }

    if (!formData.password) {
      setErrorMessage("Please enter a new password.");
      return;
    }

    if (formData.password.length < 4) {
      setErrorMessage("Password must be at least 4 characters.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrorMessage("Passwords do not match. Please verify your new password.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const response = await post("/auth/reset-password", {
        token: formData.token.trim(),
        email: formData.email.trim(),
        password: formData.password,
      });

      if (response && response.success !== false) {
        setSuccessMessage("Password reset successfully! Redirecting to login...");
        setTimeout(() => {
          props.history.push("/login");
        }, 1800);
      } else {
        setErrorMessage(response?.message || response?.error || "Failed to reset password.");
      }
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to reset password. Please verify your token.";
      setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: <RiShieldKeyholeLine />,
      title: "Strong Cryptographic Access",
      desc: "Enhanced AES-256 session and password protection",
    },
    {
      icon: <RiCheckDoubleLine />,
      title: "Instant Session Sync",
      desc: "Instant password update across all OPD clinic devices",
    },
    {
      icon: <RiKeyLine />,
      title: "Token Validation",
      desc: "Single-use verified reset link authentication",
    },
    {
      icon: <RiShieldCheckLine />,
      title: "HIPAA & EHR Safe",
      desc: "Full patient privacy and compliance standard",
    },
  ];

  return (
    <React.Fragment>
      <MetaTags>
        <title>Reset Password | Oralstop OPD - Clinical Management System</title>
      </MetaTags>

      <div className="dental-auth-wrapper">
        <div className="dental-auth-card">
          {/* Left Hero Panel */}
          <div className="dental-hero-panel">
            <div>
              {/* Brand Logo & Header */}
              <div className="dental-brand-header">
                <img src={oralstopLogo} alt="oralstop.com" height="38" className="dental-brand-logo-img" />
                <div>
                  <span className="dental-brand-sub">Clinical EHR Platform</span>
                </div>
              </div>

              {/* Main Heading */}
              <h2 className="dental-hero-heading">
                Set New <span>Security Credentials</span>.
              </h2>
              <p className="dental-hero-desc">
                Choose a strong new password for your doctor account to securely access patient charts, appointments, and billing.
              </p>

              {/* Clinical Feature List */}
              <div className="dental-features-list">
                {features.map((feat, idx) => (
                  <div key={idx} className="dental-feature-item">
                    <div className="dental-feature-icon">{feat.icon}</div>
                    <div className="dental-feature-text">
                      <h6>{feat.title}</h6>
                      <p>{feat.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Security Badge */}
            <div className="dental-security-badge">
              <span className="pulse-dot" />
              <RiShieldCheckLine className="me-1" />
              <span>256-bit AES Encrypted & HIPAA Compliant</span>
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="dental-form-panel">
            <div className="dental-form-container">
              <div className="dental-form-header">
                <h3 className="dental-form-title">Reset Password</h3>
                <p className="dental-form-subtitle">
                  Enter your verification token and create a new secure password.
                </p>
              </div>

              {/* Alerts */}
              {successMessage && (
                <Alert color="success" className="mb-3 d-flex align-items-center py-2 px-3">
                  <i className="mdi mdi-check-circle-outline me-2 font-size-16" />
                  <div className="font-size-13">{successMessage}</div>
                </Alert>
              )}

              {errorMessage && (
                <Alert color="danger" className="mb-3 d-flex align-items-center py-2 px-3">
                  <i className="mdi mdi-alert-circle-outline me-2 font-size-16" />
                  <div className="font-size-13">{errorMessage}</div>
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                {/* Reset Token Field */}
                <div className="custom-auth-group">
                  <label className="custom-auth-label" htmlFor="token-input">
                    Security Reset Token or Registered Email
                  </label>
                  <div className="custom-input-box">
                    <RiKeyLine className="input-icon-prefix" />
                    <input
                      id="token-input"
                      name="token"
                      type="text"
                      className="form-control dental-input-field"
                      placeholder="Paste reset token or email"
                      value={formData.token || formData.email}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          token: val,
                          email: val.includes("@") ? val : prev.email,
                        }));
                      }}
                      required
                    />
                  </div>
                </div>

                {/* New Password Field */}
                <div className="custom-auth-group">
                  <label className="custom-auth-label" htmlFor="password-input">
                    New Password
                  </label>
                  <div className="custom-input-box">
                    <RiLockPasswordLine className="input-icon-prefix" />
                    <input
                      id="password-input"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      className="form-control dental-input-field"
                      placeholder="Enter new password"
                      value={formData.password}
                      onChange={handleChange}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password Field */}
                <div className="custom-auth-group">
                  <label className="custom-auth-label" htmlFor="confirm-password-input">
                    Confirm New Password
                  </label>
                  <div className="custom-input-box">
                    <RiLockPasswordLine className="input-icon-prefix" />
                    <input
                      id="confirm-password-input"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      className="form-control dental-input-field"
                      placeholder="Re-enter new password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label="Toggle confirm password visibility"
                    >
                      {showConfirmPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="dental-submit-btn mt-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span>Resetting password...</span>
                  ) : (
                    <>
                      <span>Update & Reset Password</span>
                      <RiArrowRightLine className="font-size-16" />
                    </>
                  )}
                </button>
              </form>

              {/* Footer */}
              <div className="auth-footer-text">
                <span>Remember your credentials?</span>
                <Link to="/login" className="signup-link">
                  Sign In
                </Link>
                <div className="mt-2 text-muted font-size-11">
                  © {new Date().getFullYear()} Oralstop OPD · All Rights Reserved
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

ResetPassword.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object,
};

export default withRouter(ResetPassword);
